import { z } from 'zod';

import { request } from '@/api/httpClient';

const addressSchema = z
  .object({
    street_number: z.string().nullable().optional(),
    route: z.string().nullable().optional(),
    locality: z.string().nullable().optional(),
    postal_code: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    administrative_area_level_1: z.string().nullable().optional(),
    additional_information: z.string().nullable().optional(),
    latitude: z.union([z.number(), z.string()]).nullable().optional(),
    longitude: z.union([z.number(), z.string()]).nullable().optional(),
  })
  .nullable()
  .optional();
const locationSchema = z.object({
  id: z.number().int().positive(),
  client_id: z.number().int().positive(),
  name: z.string(),
  gate_code: z.string().nullable().optional(),
  address: addressSchema,
});
const bodySchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  volume: z.union([z.number(), z.string()]).nullable().optional(),
  volume_unit: z.string().nullable().optional(),
  width: z.union([z.number(), z.string()]).nullable().optional(),
  length: z.union([z.number(), z.string()]).nullable().optional(),
  depth: z.union([z.number(), z.string()]).nullable().optional(),
});
const reportSchema = z.object({
  id: z.number().int().positive(),
  created_at: z.string(),
  completed_at: z.string().nullable().optional(),
  notes_for_management: z.string().nullable().optional(),
  notes_for_client: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  readings: z.string().nullable().optional(),
  dosages: z.string().nullable().optional(),
  checklists: z.string().nullable().optional(),
  checklist_items_complete: z.string().nullable().optional(),
  user: z.object({ name: z.string() }).nullable().optional(),
  photos: z
    .array(
      z.object({
        src: z.string(),
        msrc: z.string().optional(),
        title: z.string().optional(),
        w: z.number().optional(),
        h: z.number().optional(),
      }),
    )
    .default([]),
});
export type ServiceLocation = z.infer<typeof locationSchema>;
export type BodyOfWater = z.infer<typeof bodySchema>;
export type ServiceReport = z.infer<typeof reportSchema>;
const assignmentSchema = z.object({
  id: z.number().int().positive(),
  user_id: z.number().int().positive(),
  start_date: z.string(),
  end_date: z.string().nullable().optional(),
  frequency: z.string(),
  notes: z.string().nullable().optional(),
  user: z.object({ name: z.string() }).nullable().optional(),
});
export type RouteAssignment = z.infer<typeof assignmentSchema>;
const root = (c: number, l: number) => `/managers/clients/${c}/service-locations/${l}`;
export async function getServiceLocation(c: number, l: number) {
  return locationSchema.parse(await request<unknown>(root(c, l)));
}
export async function updateServiceLocation(
  c: number,
  l: number,
  name: string,
  gateCode: string,
  address?: NonNullable<ServiceLocation['address']>,
) {
  await request(root(c, l), {
    method: 'PUT',
    body: { name, gate_code: gateCode || null, ...(address ? { address } : {}) },
  });
}
export async function deleteServiceLocation(c: number, l: number) {
  await request(root(c, l), { method: 'DELETE' });
}
export async function getLocationBodies(c: number, l: number) {
  return z.array(bodySchema).parse(await request<unknown>(`${root(c, l)}/bodies-of-water`));
}
export interface BodyOfWaterInput {
  name: string;
  width: number | null;
  length: number | null;
  depth: number | null;
  volume: number | null;
  volumeUnit: 'gallons' | 'litres';
}
export async function saveBodyOfWater(c: number, l: number, value: BodyOfWaterInput, id?: number) {
  const scaled = (number: number | null) => (number === null ? null : Math.round(number * 100));
  await request(`${root(c, l)}/bodies-of-water${id ? `/${id}` : ''}`, {
    method: id ? 'PUT' : 'POST',
    body: {
      name: value.name,
      width: scaled(value.width),
      length: scaled(value.length),
      depth: scaled(value.depth),
      volume: scaled(value.volume),
      volume_unit: value.volumeUnit,
    },
  });
}
export async function deleteBodyOfWater(c: number, l: number, id: number) {
  await request(`${root(c, l)}/bodies-of-water/${id}`, { method: 'DELETE' });
}
export async function getLocationReports(c: number, l: number) {
  return z.array(reportSchema).parse(await request<unknown>(`${root(c, l)}/service-reports`));
}
export async function getRouteAssignments(c: number, l: number) {
  return z.array(assignmentSchema).parse(await request<unknown>(`${root(c, l)}/route-assignments`));
}
export async function getOneOffStops(c: number, l: number) {
  return z.array(assignmentSchema).parse(await request<unknown>(`${root(c, l)}/one-off-stops`));
}
export async function getUpcomingServices(c: number, l: number) {
  const response = z
    .record(z.string(), z.array(z.unknown()))
    .parse(await request<unknown>(`${root(c, l)}/upcoming-services`));
  return Object.entries(response)
    .filter(([, stops]) => stops.length > 0)
    .map(([serviceDate]) => serviceDate);
}
export interface AssignmentInput {
  userId: number;
  startDate: string;
  endDate: string | null;
  frequency: string;
}
export async function saveRouteAssignment(
  c: number,
  l: number,
  value: AssignmentInput,
  id?: number,
) {
  await request(`${root(c, l)}/route-assignments${id ? `/${id}` : ''}`, {
    method: id ? 'PUT' : 'POST',
    body: {
      user_id: value.userId,
      start_date: value.startDate,
      end_date: value.endDate,
      frequency: value.frequency,
    },
  });
}
export async function deleteRouteAssignment(c: number, l: number, id: number) {
  await request(`${root(c, l)}/route-assignments/${id}`, { method: 'DELETE' });
}
export async function saveOneOffStop(
  c: number,
  l: number,
  userId: number,
  date: string,
  id?: number,
  notes = '',
) {
  await request(`${root(c, l)}/one-off-stops${id ? `/${id}` : ''}`, {
    method: id ? 'PUT' : 'POST',
    body: { user_id: userId, start_date: date, end_date: date, notes: notes.trim() || null },
  });
}
export async function deleteOneOffStop(c: number, l: number, id: number) {
  await request(`${root(c, l)}/one-off-stops/${id}`, { method: 'DELETE' });
}
